import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import {
    FiDollarSign,
    FiEdit,
    FiEye,
    FiMapPin,
    FiPhone,
    FiPlus,
    FiSearch,
    FiTrash2,
    FiTruck,
    FiUser
} from 'react-icons/fi';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ApiService from '../../services/api';

const SellerManagement = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    pending: 0,
    totalRevenue: 0
  });

  useEffect(() => {
    fetchSellers();
  }, []);

  const fetchSellers = async () => {
    try {
      setLoading(true);
      const response = await ApiService.get('/sellers', { limit: 100 });
      
      if (response.success && response.data.sellers) {
        const sellersData = response.data.sellers;
        setSellers(sellersData);

        // Calculate stats
        const active = sellersData.filter(s => s.isActive).length;
        const pending = sellersData.filter(s => !s.isVerified).length;
        
        setStats({
          total: sellersData.length,
          active: active,
          pending: pending,
          totalRevenue: 0 // Revenue tracking not implemented yet
        });
      }
    } catch (error) {
      console.error('Error fetching sellers:', error);
      toast.error('Failed to fetch sellers');
    } finally {
      setLoading(false);
    }
  };

  const handleViewSeller = async (sellerId) => {
    // Navigate to dedicated seller detail page
    navigate(`/dashboard/sellers/${sellerId}`);
  };

  const handleEditSeller = (sellerId) => {
    // Navigate to add seller page with seller ID for editing
    navigate(`/dashboard/sellers/add?edit=${sellerId}`);
  };

  const handleDeleteSeller = async (sellerId, sellerName) => {
    if (!window.confirm(`Are you sure you want to delete seller "${sellerName}"? This will deactivate their account.`)) {
      return;
    }

    try {
      const response = await ApiService.delete(`/sellers/${sellerId}`);
      if (response.success) {
        toast.success('Seller deleted successfully');
        // Remove from local state
        setSellers(prevSellers => prevSellers.filter(s => s._id !== sellerId));
        // Update stats
        setStats(prev => ({
          ...prev,
          total: prev.total - 1,
          active: prev.active - (sellers.find(s => s._id === sellerId)?.isActive ? 1 : 0)
        }));
      }
    } catch (error) {
      console.error('Error deleting seller:', error);
      toast.error('Failed to delete seller');
    }
  };

  const filteredSellers = sellers.filter(seller => {
    const fullName = `${seller.firstName} ${seller.lastName}`.toLowerCase();
    const location = `${seller.address?.city || ''}, ${seller.address?.state || ''}`.toLowerCase();
    
    const matchesSearch = fullName.includes(searchTerm.toLowerCase()) ||
                         seller.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         location.includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterStatus === 'all' || 
                         (filterStatus === 'active' && seller.isActive) ||
                         (filterStatus === 'inactive' && !seller.isActive) ||
                         (filterStatus === 'pending' && !seller.isVerified);
    
    return matchesSearch && matchesFilter;
  });

  const getStatusColor = (seller) => {
    if (!seller.isVerified) return 'bg-yellow-100 text-yellow-800';
    if (seller.isActive) return 'bg-green-100 text-green-800';
    return 'bg-gray-100 text-gray-800';
  };

  const getStatus = (seller) => {
    if (!seller.isVerified) return 'pending';
    if (seller.isActive) return 'active';
    return 'inactive';
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
          <p className="mt-4 text-gray-600">Loading sellers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Seller Management</h1>
          <p className="mt-2 text-gray-600">Manage seller accounts and their performance</p>
        </div>
        <div className="mt-4 sm:mt-0">
          <Link to="/dashboard/sellers/add">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center px-6 py-3 gradient-bg text-white font-medium rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
            >
              <FiPlus className="mr-2 h-5 w-5" />
              Add Seller
            </motion.button>
          </Link>
        </div>
      </motion.div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl p-6 card-shadow"
        >
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FiUser className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Sellers</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl p-6 card-shadow"
        >
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <FiUser className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.active}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl p-6 card-shadow"
        >
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <FiUser className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Pending</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.pending}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl p-6 card-shadow"
        >
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 rounded-lg">
              <FiDollarSign className="h-6 w-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Revenue</p>
              <p className="text-2xl font-semibold text-gray-900">
                ₹{(stats.totalRevenue / 10000000).toFixed(1)}Cr
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Filters and Search */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-white rounded-xl p-6 card-shadow"
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Search sellers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full sm:w-80 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="pending">Pending</option>
            </select>
          </div>
        </div>
      </motion.div>

      {/* Sellers List */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {filteredSellers.map((seller, index) => (
          <motion.div
            key={seller._id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            whileHover={{ scale: 1.02 }}
            className="bg-white rounded-xl p-6 card-shadow hover:shadow-xl transition-all duration-300"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 rounded-full gradient-bg flex items-center justify-center text-white font-semibold">
                  {getInitials(seller.firstName, seller.lastName)}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{seller.firstName} {seller.lastName}</h3>
                  <p className="text-gray-600">{seller.email}</p>
                </div>
              </div>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(seller)}`}>
                {getStatus(seller).charAt(0).toUpperCase() + getStatus(seller).slice(1)}
              </span>
            </div>

            <div className="space-y-3">
              <div className="flex items-center text-gray-600">
                <FiPhone className="h-4 w-4 mr-2" />
                <span className="text-sm">{seller.phone}</span>
              </div>
              <div className="flex items-center text-gray-600">
                <FiMapPin className="h-4 w-4 mr-2" />
                <span className="text-sm">
                  {seller.address?.city && seller.address?.state 
                    ? `${seller.address.city}, ${seller.address.state}`
                    : 'No location provided'}
                </span>
              </div>
              {seller.businessName && (
                <div className="flex items-center text-gray-600">
                  <FiUser className="h-4 w-4 mr-2" />
                  <span className="text-sm">{seller.businessName}</span>
                </div>
              )}
            </div>

            <div className="grid grid-cols-3 gap-4 mt-6 pt-4 border-t border-gray-100">
              <div className="text-center">
                <div className="flex items-center justify-center mb-1">
                  <FiTruck className="h-4 w-4 text-blue-600 mr-1" />
                </div>
                <p className="text-lg font-semibold text-gray-900">{seller.carsCount || 0}</p>
                <p className="text-xs text-gray-500">Total Cars</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center mb-1">
                  <FiDollarSign className="h-4 w-4 text-green-600 mr-1" />
                </div>
                <p className="text-lg font-semibold text-gray-900">{seller.carsSold || 0}</p>
                <p className="text-xs text-gray-500">Cars Sold</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center mb-1">
                  <span className="text-blue-500">●</span>
                </div>
                <p className="text-lg font-semibold text-gray-900">{seller.carsActive || 0}</p>
                <p className="text-xs text-gray-500">Active</p>
              </div>
            </div>

            {seller.agentId && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  Created by: <span className="font-semibold">{seller.agentId.fullName || seller.agentId.firstName}</span>
                </p>
              </div>
            )}

            <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-100">
              <p className="text-sm text-gray-500">
                Joined: {new Date(seller.createdAt).toLocaleDateString()}
              </p>
              <div className="flex space-x-2">
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => handleViewSeller(seller._id)}
                  title="View Details"
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                >
                  <FiEye className="h-4 w-4" />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => handleEditSeller(seller._id)}
                  title="Edit Seller"
                  className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                >
                  <FiEdit className="h-4 w-4" />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => handleDeleteSeller(seller._id, `${seller.firstName} ${seller.lastName}`)}
                  title="Delete Seller"
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <FiTrash2 className="h-4 w-4" />
                </motion.button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {filteredSellers.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12"
        >
          <p className="text-gray-500 text-lg">No sellers found matching your criteria.</p>
        </motion.div>
      )}
    </div>
  );
};

export default SellerManagement;